<template>
    <form id="sbParkingForm" class="form" @submit.prevent="submitForm()">
        <div class="airport-preloaded">
            <div class="icon">
                <img src="~/assets/images/icon-parking.webp" alt="airport-parking-icon" loading="eager" />
            </div>
            <div v-if="!tabs.Title" class="content">
                <span class="hidden lg:block">{{
                    formData.destination?.label?.length <= 10
                        ? formData.destination.label
                        : formData.destination?.code?.length === 5
                          ? formData.destination.code.substring(2)
                          : formData.destination.code
                }}</span>
                <span class="inline-block lg:hidden">{{ formData.destination?.label?.replace(' Port', '') }}</span>
                {{ searchStore.isCruiseport || formData.destination?.code?.length === 5 ? 'Port' : 'Airport' }}
                Parking
            </div>
            <div v-else class="content">{{ tabs.Title }}</div>
        </div>
        <SearchboxComponentsDateTimePicker
            :label="{ date: 'Drop Off Date', time: 'Time' }"
            name="dropoff"
            :min-date="new Date()"
            :min-time="minEntryTime"
            :value="formData?.dates?.start"
            :validation="v$?.dates?.start"
            :allow-single-range="true"
            @update:value="(value: any) => updateEntryDate(value)"
        />
        <SearchboxComponentsDateTimePicker
            :label="{ date: 'Pick Up Date', time: 'Time' }"
            name="pickup"
            :min-date="new Date(formData.dates.start?.date) ?? new Date()"
            :min-time="minToTime"
            :value="formData?.dates?.end"
            :validation="v$.dates.end"
            :allow-single-range="true"
            @update:value="(value: any) => updateExitDates(value)"
        />
        <SearchboxComponentsSimpleInput
            v-if="showPromo"
            title="Promo Code"
            placeholder="Enter Code"
            name="promocode"
            :value="formData.promo"
            @update:value="(value: any) => updatePromo(value)"
        />
        <div class="submit flex-1 ml-4">
            <button type="submit" :disabled="!isHydrated">
                <template v-if="!loading">Search <span class="fullname">Parking</span></template>
                <NuxtIcon v-show="loading" name="ri:refresh-line" class="animate-spin loading-icon" />
            </button>
        </div>
    </form>
</template>

<script setup lang="ts">
import { useVuelidate } from '@vuelidate/core';
import type { Validation } from '@vuelidate/core';
import { required, minLength, helpers } from '@vuelidate/validators';
import { useSearchStore } from '@nuxt-monorepo/base/app/store/search';
// COMMENTED OUT: Legacy search
// import { formatDate } from '@nuxt-monorepo/ui/utils/helpers';

const emit = defineEmits<{
    (e: 'toggleLoading'): void;
}>();

// Define local types since external types are not found
interface ISearchboxTab {
    Extras?: boolean;
    [key: string]: any;
}

interface IAirportParking {
    category: string;
    airportA: number;
    airportA_code: string;
    dateA: string;
    dateB: string;
    _dateA: string;
    _dateB: string;
    timeA: string;
    timeB: string;
    promo: string;
    passengers: number;
    agent: string;
    params: any[];
    terminal: string;
    quoteID: string;
    nohotel: number;
    park_type: string;
}

// Import traffic splitter functions directly
import { redirectToParkingPropel } from '~/mixins/redirectToPropel';
import {
    differenceInDaysWithoutTime,
    isEndDateBeforeStartDate,
    nextTimeSlot,
} from '@nuxt-monorepo/ui/utils/dateTimeHelper';

// Define prop and emit types
const {
    loading = false,
    defaultPromo = '',
    defaultAirport = '',
    airportList = [],
    showPromo = false,
    tabs = {},
} = defineProps<{
    tabs?: ISearchboxTab;
    airportList?: any[];
    defaultPromo?: string;
    defaultAirport?: string;
    showPromo?: boolean;
    loading?: boolean;
}>();

// COMMENTED OUT: Legacy search
// const emit = defineEmits<{
//     (e: 'submitSearch', query: IAirportParking): void;
// }>();

// Constant variable for access to search store
const searchStore = useSearchStore();
const isHydrated = ref(false);

// Utility function to get days ahead
const getDateDaysAhead = (date: Date, days: number) => {
    const newDate = new Date(date);
    newDate.setDate(newDate.getDate() + days);
    return newDate;
};

// Define form data type with more flexible date handling
interface FormData {
    destination: any;
    dates: {
        start: {
            date: Date;
            time: string;
            timeRange?: { value: string; label?: string };
        };
        end: {
            date: Date;
            time: string;
        };
    };
    promo: string;
    guests?: {
        adult?: number;
        children?: number;
        infants?: number;
    };
}

const updateEntryDate = (val: any) => {
    formData.value.dates.start.date = val.date;
    formData.value.dates.start.time = val.time;
    validateUpdatedDate();

    searchStore.update({ dates: formData.value.dates });
};

const updateExitDates = (val: any) => {
    formData.value.dates.end.date = val.date;
    formData.value.dates.end.time = val.time;
    validateUpdatedDate();

    searchStore.update({ dates: formData.value.dates });
};

const updatePromo = (val: any) => {
    formData.value.promo = val;
    searchStore.updatePromo(val);
};

// Helper function to normalize dates
const normalizeDates = (dates: any) => {
    // If dates is a simple object with start/end strings, convert to expected structure
    if (typeof dates.start === 'string' && typeof dates.end === 'string') {
        return {
            start: {
                date: new Date(dates.start),
                time: '12:00',
                timeRange: { value: '12:00 - 13:00', label: '12:00 - 13:00' },
            },
            end: {
                date: new Date(dates.end),
                time: '12:00',
            },
        };
    }

    // If dates already match the expected structure, return as-is
    return dates;
};

const formData = ref<FormData>({
    destination: getDefaultAirport(),
    dates: searchStore.dates?.start ? normalizeDates(searchStore.dates) : setDefaultDates(),
    promo: defaultPromo ?? '',
    guests: {
        adult: 1,
        children: 0,
        infants: 0,
    },
});

function getDefaultAirport() {
    if (defaultAirport) {
        return airportList.find((airport) => airport.code === defaultAirport);
    }

    if (searchStore.destination?.airport_code) {
        return searchStore.destination;
    }

    return { code: '', label: 'Select Airport', value: 0, disabled: false };
}

function setDefaultDates() {
    const defaultStartDate = {
        date: getDateDaysAhead(new Date(), 1),
        time: '12:00',
        timeRange: { value: '12:00 - 13:00', label: '12:00 - 13:00' },
    };

    const defaultEndDate = {
        date: getDateDaysAhead(new Date(), 8),
        time: '12:00',
    };

    const defaultDates = {
        start: defaultStartDate,
        end: defaultEndDate,
    };

    searchStore.update({ dates: defaultDates });
    return defaultDates;
}

const minEntryTime = computed(() => {
    const minDate = new Date(new Date(formData.value.dates.start.date).toDateString());
    const today = new Date();
    if (differenceInDaysWithoutTime(today, minDate) > 0) {
        return '';
    }

    const now = new Date();
    return `${now.getHours()}:${now.getMinutes()}`;
});

const minToTime = computed(() => {
    const minDate = new Date(new Date(formData.value.dates.start.date).toDateString());
    const toDate = new Date(new Date(formData.value.dates.end.date).toDateString());
    if (differenceInDaysWithoutTime(minDate, toDate) > 0) {
        return '';
    }

    return formData.value.dates.start.time;
});

const option = computed(() => {
    return {
        id: 'APR',
        name: 'At the parkinglot',
        additional: '',
        start_date: {
            title: 'Parking Check-In',
            tooltip: 'Date when your parking begins',
            placeholder: 'Select Date',
        },
        end_date: {
            title: 'Parking Check-Out',
            tooltip: 'Date when your parking ends',
            placeholder: 'Select Date',
        },
    };
});

const beforeCurrentTime = () => {
    const currentTime = new Date();
    const startDate = new Date(formData.value.dates.start.date);
    const startTime = formData.value.dates.start.time.split(':');
    startDate.setHours(parseInt(startTime[0]));
    startDate.setMinutes(parseInt(startTime[1]));
    return startDate > currentTime;
};

const beforeStartDate = () => {
    return isEndDateBeforeStartDate(formData.value.dates.start, formData.value.dates.end);
};

const rules = computed(() => {
    return {
        destination: {
            code: {
                required: helpers.withMessage('Please enter destination', required),
                minLength: minLength(3),
            },
        },
        dates: {
            start: {
                date: {
                    required: helpers.withMessage('Select Date', required),
                    beforeCurrentTime: helpers.withMessage(
                        'Drop Off Date cannot be before the current time',
                        beforeCurrentTime
                    ),
                },
                time: { required: helpers.withMessage('Select Time', required) },
            },
            end: {
                date: {
                    required: helpers.withMessage('Select Date', required),
                    beforeStartDate: helpers.withMessage('Drop Off Date must be before Pick Up Date.', beforeStartDate),
                },
                time: { required: helpers.withMessage('Select Time', required) },
            },
        },
    };
});

// Add type safety for validation
const v$ = useVuelidate(rules, formData) as Ref<Validation>;

const validateUpdatedDate = () => {
    if (differenceInDaysWithoutTime(new Date(), new Date(formData.value.dates.start.date)) <= 0) {
        const now = new Date();
        if (formData.value.dates.start.time <= `${now.getHours()}:${now.getMinutes()}`) {
            formData.value.dates.start.time = nextTimeSlot(`${now.getHours()}:${now.getMinutes()}`, 60);
        }
    }

    if (!isEndDateBeforeStartDate(formData.value.dates.start, formData.value.dates.end)) {
        formData.value.dates.end.date = formData.value.dates.start.date;
        formData.value.dates.end.time = nextTimeSlot(formData.value.dates.start.time, 60);
    }
};

onMounted(async () => {
    isHydrated.value = true;
});

const submitForm = async () => {
    v$.value.$validate();

    if (!v$.value.$error) {
        emit('toggleLoading');

        searchStore.update({
            destination: formData.value.destination,
            dates: formData.value.dates,
            guests: formData.value.guests,
        });

        redirectToParkingPropel();

        // COMMENTED OUT: Legacy search
        // const query = {
        //     category: 'airportparking',
        //     airportA: Number(formData.value.destination.value),
        //     airportA_code: formData.value.destination.code,
        //     dateA: formatDate(formData.value.dates.start.date, 'yyyy-mm-dd'),
        //     dateB: formatDate(formData.value.dates.end.date, 'yyyy-mm-dd'),
        //     _dateA: formatDate(formData.value.dates.start.date, 'day, dd/mm/yy'),
        //     _dateB: formatDate(formData.value.dates.end.date, 'day, dd/mm/yy'),
        //     timeA: formData.value.dates.start.time,
        //     timeB: formData.value.dates.end.time,
        //     promo: formData.value.promo,
        //     passengers: 1,
        //     agent: 'ONLINE',
        //     params: [],
        //     terminal: '',
        //     quoteID: '',
        //     nohotel: 0,
        //     park_type: 'all',
        // };

        // emit('submitSearch', query);
    }
};

watch(
    () => searchStore?.promo,
    (value) => {
        if (value && value !== formData.value.promo) {
            formData.value.promo = value;
        }
    }
);
</script>

<style lang="postcss" scoped>
.airport-preloaded {
    @apply flex items-center gap-4 mb-4;
    .icon {
        @apply w-[50px] h-[50px];
        img {
            @apply w-full h-full object-contain;
        }
    }
    .content {
        @apply text-lg font-bold uppercase;
    }
}
button[type='submit']:disabled {
    @apply cursor-not-allowed opacity-70;
}
</style>
